import { useCallback, useEffect, useState } from "react";
import {
  Grid,
  MenuItem,
  Select,
  Checkbox,
  Button,
  Typography,
  Stack,
  Skeleton,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { Feature } from "models/api";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IEvolutionTrainer,
  IEvolutionTrainerAcceptanceDate,
} from "models/trainer";
import { useAlert } from "contexts/alertContext";
import { QueryClient, useMutation } from "react-query";
import { changeTrainerAttendance } from "framework/trainer";
import { getVendors } from "framework/vendor";
import { handleError } from "helpers/utils";
import { getUserData } from "helpers";
import { UserRoleName } from "types/auth";
import BackButton from "components/UI/BackButton";

interface Props {
  feature: Feature;
}

const AttendanceForm: React.FC<Props> = ({ feature }) => {
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [date, setDate] = useState<{ date: string; enabled: boolean }[]>([]);
  const [allAttendance, setAllAttendance] = useState<
    IEvolutionTrainerAcceptanceDate[]
  >([]);
  const [attendance, setAttendance] = useState<
    IEvolutionTrainerAcceptanceDate[]
  >([]);
  const userData = getUserData();
  const { roleName } = userData;
  const urlParams = useParams();
  let id: string = urlParams[`${feature.singleName}Id`] as string;
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation();
  let [FilterTrainers, setFilterTrainers] = useState<IEvolutionTrainer>(
    {} as IEvolutionTrainer
  );

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const changeStatusMutation = useMutation({
    mutationFn: (createInput: any) => {
      return changeTrainerAttendance(createInput);
    },
  });

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      let response: IEvolutionTrainer = await queryClient.fetchQuery(
        "get Vendors",
        () => getVendors(`/VendorAttendnace?workshopId=${id}`)
      );
      setFilterTrainers(response);
      setDate(response.dates);
      setAllAttendance(response.attendanceData);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchVendors();
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChangeDate = (date: string) => {
    setSelectedDate(date);
    setAttendance(allAttendance.filter((x) => x.date === date));
  };

  interface GroupedAttendance {
    date: string;
    enrollmentId: string;
    attendancePercentage: number;
    sequences: number[];
    attended: boolean[]; // Ensure this is explicitly an array of booleans
    notes: string[]; // Ensure this is explicitly an array of strings
  }

  function groupAttendanceByEnrollmentAndDate(
    data: IEvolutionTrainerAcceptanceDate[]
  ): GroupedAttendance[] {
    let maxSequences = 0;

    const groupedData = data.reduce((acc, item) => {
      const key = `${item.enrollmentId}-${item.date}`;

      if (!acc[key]) {
        acc[key] = {
          date: item.date,
          enrollmentId: item.enrollmentId,
          attendancePercentage: item.attendancePercentage,
          sequences: [item.sequence],
          attended: [item.attended],
          notes: [item.note || ""], // If note is null, convert to empty string
        };
      } else {
        acc[key].sequences.push(item.sequence);
        acc[key].attended.push(item.attended);
        acc[key].notes.push(item.note || ""); // If note is null, convert to empty string
      }

      return acc;
    }, {} as Record<string, GroupedAttendance>);

    // Sort sequences along with attended and notes
    const result = Object.values(groupedData).map((group) => {
      const combined = group.sequences.map((seq, index) => ({
        sequence: seq,
        attended: group.attended[index],
        note: group.notes[index],
      }));

      // Sort combined array by sequence
      combined.sort((a, b) => a.sequence - b.sequence);

      // Extract sorted values back to separate arrays
      const sortedGroup = {
        ...group,
        sequences: combined.map((item) => item.sequence),
        attended: combined.map((item) => item.attended),
        notes: combined.map((item) => item.note),
      };

      // Track the maximum sequence length
      if (sortedGroup.sequences.length > maxSequences) {
        maxSequences = sortedGroup.sequences.length;
      }

      return sortedGroup;
    });

    return result;
  }

  const countAbsencesBySequence = (data: IEvolutionTrainerAcceptanceDate[]) => {
    const absenceCounts: any = {};

    data.forEach((entry) => {
      const { sequence, attended } = entry;

      // Initialize the count for this sequence if it doesn't exist
      if (!absenceCounts[sequence]) {
        absenceCounts[sequence] = { absences: 0, total: 0 };
      }

      // Increment the total count for this sequence
      absenceCounts[sequence].total++;

      // Increment the absence count if not attended
      if (!attended) {
        absenceCounts[sequence].absences++;
      }
    });

    // Format the output as "absences/total"
    const formattedCounts: any = {};
    for (const seq in absenceCounts) {
      formattedCounts[
        seq
      ] = `${absenceCounts[seq].absences}/${absenceCounts[seq].total}`;
    }

    return formattedCounts;
  };

  const groupedAttendance = groupAttendanceByEnrollmentAndDate(attendance);

  // Sort trainees alphabetically by name
  const sortedGroupedAttendance = [...groupedAttendance].sort((a, b) => {
    const nameA = FilterTrainers.enrollments.find(
      (item) => item.enrollmentId === a.enrollmentId
    )?.employeeName || '';
    const nameB = FilterTrainers.enrollments.find(
      (item) => item.enrollmentId === b.enrollmentId
    )?.employeeName || '';
    return nameA.localeCompare(nameB);
  });

  const absenceCounts = countAbsencesBySequence(attendance);
  // const handleAttendanceChange = (
  //   traineeIndex: number,
  //   type: string,
  //   value: boolean | string
  // ) => {
  //   setAttendance((prevAttendance) => {
  //     const updatedAttendance = [...prevAttendance];
  //     const trainee = updatedAttendance[traineeIndex];

  //     if (type.startsWith("attendance")) {
  //       const seqIndex = parseInt(type.replace("attendance", "")) - 1; // Get the sequence index
  //       trainee.attended[seqIndex] = value as boolean; // Update attended status
  //     } else if (type.startsWith("note")) {
  //       const seqIndex = parseInt(type.replace("note", "")) - 1; // Get the sequence index
  //       trainee.notes[seqIndex] = value as string; // Update note
  //     }

  //     return updatedAttendance;
  //   });
  // };

  const handleCheckboxChange = (
    checked: boolean,
    enrollmentId: string,
    seq: number
  ) => {
    setAttendance((prevAttendance) =>
      prevAttendance.map(
        (entry) =>
          entry.enrollmentId === enrollmentId && entry.sequence === seq
            ? { ...entry, attended: checked } // Update the note
            : entry // Keep the existing entry
      )
    );
  };


  const handleSelectAll = (checked: boolean, enrollmentId: string) => {
    setAttendance((prevAttendance) =>
      prevAttendance.map((entry) =>
        entry.enrollmentId === enrollmentId
          ? {
            ...entry,
            attended: entry.attended = checked, // Set all sequences to the same checked value
          }
          : entry
      )
    );
  };


  const handleNoteChange = (
    note: string,
    enrollmentId: string,
    seq: number
  ) => {
    setAttendance((prevAttendance) =>
      prevAttendance.map(
        (entry) =>
          entry.enrollmentId === enrollmentId && entry.sequence === seq
            ? { ...entry, note } // Update the note
            : entry // Keep the existing entry
      )
    );
  };

  const getChangedAttendance = (
    allAttendance: IEvolutionTrainerAcceptanceDate[],
    attendance: IEvolutionTrainerAcceptanceDate[]
  ) => {
    const changedRecords: IEvolutionTrainerAcceptanceDate[] = [];

    attendance.forEach((modified) => {
      const original = allAttendance.find(
        (item) => item.enrollmentId === modified.enrollmentId
      );
      if (original) {
        // Check only the 'attended' and 'note' fields for changes
        if (
          original.attended !== modified.attended ||
          original.note !== modified.note
        ) {
          changedRecords.push(modified);
        }
      }
    });

    return changedRecords;
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      await changeStatusMutation.mutateAsync(
        attendance
      );
      setAlert({
        open: true,
        message: "Status Changed successfully",
        type: "success",
      });
      fetchVendors();
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  };
  const currDate = new Date(selectedDate);
  currDate.setDate(currDate.getDate() + 1);
  const pastDay = currDate < new Date();

  return (
    <div style={{ padding: "20px 30px" }}>
      <BackButton 
        path={`/workshops/${id}`} 
        text={t('T_back_to_workshop_details') || 'Back to Workshop Details'} 
      />
      
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography>{t('T_select_date')}</Typography>
          <Select
            placeholder={t('T_select_date')}
            fullWidth
            value={selectedDate}
            onChange={(e) => handleChangeDate(e.target.value)}
          >
            {date.map((date, idx) => (
              <MenuItem key={idx} value={date.date}>
                {date.date}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      {selectedDate && (
        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>

                {/* Render headers for each sequence */}
                {sortedGroupedAttendance.length > 0 &&
                  sortedGroupedAttendance[0].sequences.map((_, seqIndex) => (
                    <TableCell key={seqIndex}>
                      Absence {absenceCounts[seqIndex + 1]}
                    </TableCell>
                  ))}
              </TableRow>
              <TableRow>
                <TableCell>Trainee Name</TableCell>

                {/* Render headers for each sequence */}
                {sortedGroupedAttendance.length > 0 &&
                  sortedGroupedAttendance[0].sequences.map((_, seqIndex) => (
                    <TableCell key={seqIndex}>
                      Attendance {seqIndex + 1}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedGroupedAttendance.map((trainee, index) => (
                <TableRow key={index}>
                  {/* Trainee Name */}
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 35, justifyContent: 'center' }}>

                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <Checkbox
                          checked={trainee.attended.every((attended) => attended)} // All sequences are checked
                          indeterminate={
                            trainee.attended.some((attended) => attended) &&
                            !trainee.attended.every((attended) => attended)
                          } // Some but not all are checked
                          onChange={(e) =>
                            handleSelectAll(e.target.checked, trainee.enrollmentId)
                          }
                          disabled={roleName !== UserRoleName.TRAINER && pastDay}
                        />
                        <Typography variant="body2" >
                          Check All
                        </Typography>
                      </div>
                      <div>
                        {
                          FilterTrainers.enrollments.find(
                            (item) => item.enrollmentId === trainee.enrollmentId
                          )?.employeeName
                        }
                      </div>
                    </div>


                  </TableCell>

                  {trainee.sequences.map((_, seqIndex) => (
                    <TableCell key={seqIndex} align="center">
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                          marginBlock: "10px",
                        }}
                      >
                        <Checkbox
                          checked={trainee.attended[seqIndex]}
                          onChange={(e) =>
                            handleCheckboxChange(
                              !trainee.attended[seqIndex],
                              trainee.enrollmentId,
                              seqIndex + 1
                            )
                          }
                          disabled={
                            roleName !== UserRoleName.TRAINER && pastDay
                          }
                        />
                        {trainee.attended[seqIndex] ? (
                          <Typography variant="body2" color="green">
                            Present
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="red">
                            Absent
                          </Typography>
                        )}
                      </div>

                      <TextField
                        label="Note"
                        value={trainee.notes[seqIndex] || ""}
                        fullWidth
                        onChange={(e) =>
                          handleNoteChange(
                            e.target.value,
                            trainee.enrollmentId,
                            seqIndex + 1
                          )
                        }
                        disabled={roleName !== UserRoleName.TRAINER && pastDay}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedDate && roleName === UserRoleName.TRAINER && (
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          sx={{ marginTop: 2 }}
        >
          Save
        </Button>
      )}
    </div>
  );
};

export default AttendanceForm;
